/**
 * Gemeinsame Popup-Positionierung für DropDown, Tooltip, Kalender und Dialog
 * (REQ-06 §2/§4). Alle Popups liegen per Portal an `document.body` und sind in
 * den übernommenen Stilen `position: absolute` — die berechneten Werte sind
 * daher Dokumentkoordinaten.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';

export type PopupAlign =
  'BottomLeft' | 'BottomCenter' | 'BottomRight' | 'TopLeft' | 'TopCenter' | 'TopRight';

export interface PopupPositionOptions {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  popupRef: RefObject<HTMLElement | null>;
  /** Standard `BottomLeft` — wie das Standard-Popup von Syncfusion. */
  align?: PopupAlign;
  /** Popup übernimmt mindestens die Breite des Ankers (Auswahllisten). */
  matchAnchorWidth?: boolean;
  /** Abstand zwischen Anker und Popup in Pixeln. */
  offset?: number;
  /**
   * `false` unterdrückt jede Inline-Positionierung, damit CSS-Regeln greifen
   * (Profil- und Navbar-Tooltip, REQ-06 §3).
   */
  enabled?: boolean;
}

const EMPTY_STYLE: CSSProperties = {};

function horizontalOffset(align: PopupAlign, anchorWidth: number, popupWidth: number): number {
  if (align === 'BottomCenter' || align === 'TopCenter') return (anchorWidth - popupWidth) / 2;
  if (align === 'BottomRight' || align === 'TopRight') return anchorWidth - popupWidth;
  return 0;
}

export function usePopupPosition(options: PopupPositionOptions): CSSProperties {
  const { open, anchorRef, popupRef } = options;
  const align = options.align ?? 'BottomLeft';
  const matchAnchorWidth = options.matchAnchorWidth ?? false;
  const offset = options.offset ?? 0;
  const enabled = options.enabled ?? true;

  const [style, setStyle] = useState<CSSProperties>(EMPTY_STYLE);

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (anchor === null || popup === null) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const isTop = align.startsWith('Top');

    let left =
      anchorRect.left + window.scrollX + horizontalOffset(align, anchorRect.width, popupRect.width);
    const top = isTop
      ? anchorRect.top + window.scrollY - popupRect.height - offset
      : anchorRect.bottom + window.scrollY + offset;

    // innerhalb des Sichtfensters halten
    const maxLeft = window.scrollX + document.documentElement.clientWidth - popupRect.width;
    left = Math.max(window.scrollX, Math.min(left, Math.max(window.scrollX, maxLeft)));

    setStyle(matchAnchorWidth ? { left, top, minWidth: anchorRect.width } : { left, top });
  }, [align, anchorRef, matchAnchorWidth, offset, popupRef]);

  useLayoutEffect(() => {
    if (!open || !enabled) {
      setStyle(EMPTY_STYLE);
      return;
    }
    update();
  }, [enabled, open, update]);

  useEffect(() => {
    if (!open || !enabled) return;

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    // Die Liste eines Popups wächst beim Nachladen — dann neu ausrichten.
    // `ResizeObserver` fehlt in jsdom, deshalb die Prüfung.
    const popup = popupRef.current;
    let observer: ResizeObserver | null = null;
    if (popup !== null && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(popup);
    }

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [enabled, open, popupRef, update]);

  return enabled ? style : EMPTY_STYLE;
}

/**
 * Schließt ein Popup bei Klick außerhalb aller übergebenen Elemente.
 * `pointerdown` statt `click`, damit die Auswahl im Popup nicht verloren geht.
 */
export function useOutsideDismiss(
  open: boolean,
  onDismiss: () => void,
  refs: RefObject<HTMLElement | null>[],
): void {
  // Rückruf und Ref-Liste wechseln bei jedem Render die Identität; sie werden
  // deshalb nachgeführt statt als Abhängigkeit gelistet.
  const latest = useRef({ onDismiss, refs });
  useEffect(() => {
    latest.current = { onDismiss, refs };
  });

  useEffect(() => {
    if (!open) return;

    const handler = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (latest.current.refs.some((ref) => ref.current?.contains(target) === true)) return;
      latest.current.onDismiss();
    };

    document.addEventListener('pointerdown', handler);
    return () => {
      document.removeEventListener('pointerdown', handler);
    };
  }, [open]);
}
